import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Phone, Shield, Star, LogOut, Clock } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CircleDashboard() {
  const { user, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/circle/dashboard`, { withCredentials: true })
      .then((r) => { setDashboard(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen bg-bone flex items-center justify-center pt-20"><p className="font-body text-slate">Loading dashboard...</p></div>;

  const submissions = dashboard?.submissions || [];

  return (
    <>
      <Helmet><title>Dispatch Circle Dashboard | Dispatch Hunts</title></Helmet>

      {/* Header */}
      <section className="section-dark grain pt-28 pb-12" data-testid="circle-dashboard-header">
        <div className="container-dh">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-label text-brass text-[10px] tracking-wider uppercase flex items-center gap-1"><Shield className="w-3 h-3" /> Dispatch Circle</span>
                {user?.role === "admin" && <span className="text-label text-copper text-[10px] tracking-wider uppercase">Admin</span>}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-bone" data-testid="circle-dashboard-title">Welcome back, {user?.name?.split(" ")[0]}</h1>
            </div>
            <button onClick={logout} className="btn-outline !text-xs flex items-center gap-2" data-testid="circle-logout-btn">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-carbon/95 py-6" data-testid="circle-stats">
        <div className="container-dh grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="font-mono text-2xl font-bold text-brass">{user?.circle_member ? "Active" : "Pending"}</p>
            <p className="text-label text-bone/40 text-[10px]">Circle Status</p>
          </div>
          <div>
            <p className="font-mono text-2xl font-bold text-bone">${(dashboard?.user?.total_spend || 0).toLocaleString()}</p>
            <p className="text-label text-bone/40 text-[10px]">Total Spend</p>
          </div>
          <div>
            <p className="font-mono text-2xl font-bold text-bone">{submissions.length}</p>
            <p className="text-label text-bone/40 text-[10px]">Hunt Applications</p>
          </div>
          <div>
            <p className="font-mono text-2xl font-bold text-bone">
              {user?.circle_member ? <Star className="w-6 h-6 text-brass inline" /> : "—"}
            </p>
            <p className="text-label text-bone/40 text-[10px]">Priority Access</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-light py-16" data-testid="circle-content">
        <div className="container-dh">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="lg:col-span-2">
              <h2 className="font-display text-2xl font-bold text-carbon mb-6">Your Hunt Applications</h2>
              {submissions.length > 0 ? (
                <div className="space-y-4">
                  {submissions.map((sub, i) => (
                    <div key={sub.id || i} className="bg-chalk border border-carbon/10 p-5" data-testid={`submission-${i}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-display text-lg font-bold text-carbon">{sub.adventure_type || "Hunt"} Application</h3>
                          <p className="text-label text-[10px] tracking-wider uppercase text-smoke">
                            {sub.regions?.join(", ") || "Various regions"}
                          </p>
                        </div>
                        <span className={`text-label text-[10px] tracking-wider uppercase px-2 py-1 ${
                          sub.status === "new" ? "bg-brass/20 text-brass" :
                          sub.status === "contacted" ? "bg-field/20 text-field" :
                          "bg-smoke/20 text-smoke"
                        }`}>{sub.status || "new"}</span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-xs text-smoke font-body">
                        <span>Budget: {sub.budget_range || sub.budget_tier}</span>
                        {sub.species_categories && <span>Categories: {sub.species_categories.join(", ")}</span>}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />{sub.created_at ? new Date(sub.created_at).toLocaleDateString() : "Recent"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-chalk border border-carbon/10 p-8 text-center">
                  <p className="font-body text-slate mb-4">No hunt applications yet.</p>
                  <Link to="/apply" className="btn-brass" data-testid="circle-apply-first">
                    Submit Your First Application <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-chalk border border-carbon/10 p-6">
                <h3 className="font-display text-lg font-bold text-carbon mb-4">Circle Benefits</h3>
                <ul className="space-y-3 font-body text-sm text-slate">
                  <li className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-brass mt-0.5 flex-shrink-0" />
                    Priority access to limited-availability hunts
                  </li>
                  <li className="flex items-start gap-2">
                    <Phone className="w-4 h-4 text-brass mt-0.5 flex-shrink-0" />
                    Direct line to senior advisors
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-brass mt-0.5 flex-shrink-0" />
                    Annual gathering at premier lodge
                  </li>
                </ul>
              </div>

              <div className="bg-chalk border border-carbon/10 p-6">
                <h3 className="font-display text-lg font-bold text-carbon mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Link to="/apply" className="btn-brass w-full justify-center text-center !text-xs" data-testid="circle-new-hunt">
                    New Hunt Application <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/expeditions" className="btn-outline-dark w-full justify-center text-center !text-xs">
                    Browse Expeditions
                  </Link>
                  <a href="tel:4065805366" className="btn-outline-dark w-full justify-center text-center !text-xs">
                    <Phone className="w-4 h-4" /> Call: 406-580-5366
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
